<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import ReportHeader from './components/ReportHeader.vue';
import KpiFilters from './components/KpiFilters.vue';
import KpiCard from './components/KpiCard.vue';
import KpiTable from './components/KpiTable.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useAlert } from 'dashboard/composables';
import kpiReportsAPI from 'dashboard/api/kpiReports';

const { t } = useI18n();

const isLoading = ref(false);
const isExporting = ref(false);
const summary = ref({});
const weeklyData = ref([]);
const activeFilter = ref({ from: null, to: null, label: '' });

const fetchKpiData = async () => {
  if (!activeFilter.value.from || !activeFilter.value.to) return;

  isLoading.value = true;
  try {
    const response = await kpiReportsAPI.getKpiSummary({
      from: activeFilter.value.from,
      to: activeFilter.value.to,
    });
    summary.value = response.data?.summary || {};
    weeklyData.value = response.data?.weekly_data || [];
  } catch (error) {
    useAlert(t('REPORT.DATA_FETCHING_FAILED'));
  } finally {
    isLoading.value = false;
  }
};

const onFilterChange = ({ from, to, label }) => {
  activeFilter.value = { from, to, label };
  fetchKpiData();
};

const onResetFilter = () => {
  fetchKpiData();
};

const downloadExport = async () => {
  if (!activeFilter.value.from || !activeFilter.value.to) return;

  isExporting.value = true;
  try {
    const response = await kpiReportsAPI.downloadKpiExport({
      from: activeFilter.value.from,
      to: activeFilter.value.to,
    });
    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const dateStr = new Date().toISOString().slice(0, 10);
    link.setAttribute('download', `kpi_report_${dateStr}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    useAlert(t('REPORT.KPI_REPORTS.EXPORT_FAILED'));
  } finally {
    isExporting.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- Header with Export Action -->
    <ReportHeader
      :header-title="$t('REPORT.KPI_REPORTS.HEADER')"
      :header-description="$t('REPORT.KPI_REPORTS.DESCRIPTION')"
    >
      <Button
        :label="$t('REPORT.KPI_REPORTS.EXPORT_CSV')"
        icon="i-lucide-download"
        size="sm"
        variant="outline"
        color="slate"
        :is-loading="isExporting"
        @click="downloadExport"
      />
    </ReportHeader>

    <!-- Filters Panel -->
    <KpiFilters
      :is-loading="isLoading"
      @filter-change="onFilterChange"
      @reset-filter="onResetFilter"
    />

    <!-- KPI Summary Dashboard Cards -->
    <section>
      <div class="flex items-center justify-between mb-3">
        <h3 class="text-sm font-semibold text-n-slate-12 uppercase tracking-wider flex items-center gap-1.5 m-0">
          <Icon icon="i-lucide-gauge" class="w-4 h-4 text-n-brand" />
          {{ $t('REPORT.KPI_CARDS.SECTION_TITLE') }}
        </h3>
        <span class="text-xs text-n-slate-10">
          {{ $t('REPORT.KPI_CARDS.COMPARED_TO_PREVIOUS') }}
        </span>
      </div>

      <!-- 11 Core KPI Cards Grid -->
      <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3.5 mb-4">
        <!-- 1. Total Email Tickets -->
        <KpiCard
          :label="$t('REPORT.KPI_CARDS.TOTAL_EMAIL_TICKETS')"
          :value="summary.total_email_tickets?.value ?? 0"
          :previous-value="summary.total_email_tickets?.previous"
          :change-pct="summary.total_email_tickets?.change_pct"
          :info-text="$t('REPORT.KPI_CARDS.INFO_TOTAL_EMAIL_TICKETS')"
        />

        <!-- 2. Resolved Email Tickets -->
        <KpiCard
          :label="$t('REPORT.KPI_CARDS.RESOLVED_EMAIL_TICKETS')"
          :value="summary.resolved_email_tickets?.value ?? 0"
          :previous-value="summary.resolved_email_tickets?.previous"
          :change-pct="summary.resolved_email_tickets?.change_pct"
          :percentage="summary.email_resolution_rate?.value"
          :numerator="summary.resolved_email_tickets?.value"
          :denominator="summary.total_email_tickets?.value"
          :info-text="$t('REPORT.KPI_CARDS.INFO_RESOLVED_EMAIL_TICKETS')"
        />

        <!-- 3. Total Live Chats -->
        <KpiCard
          :label="$t('REPORT.KPI_CARDS.TOTAL_LIVE_CHATS')"
          :value="summary.total_live_chats?.value ?? 0"
          :previous-value="summary.total_live_chats?.previous"
          :change-pct="summary.total_live_chats?.change_pct"
          :info-text="$t('REPORT.KPI_CARDS.INFO_TOTAL_LIVE_CHATS')"
        />

        <!-- 4. Missed Live Chats -->
        <KpiCard
          :label="$t('REPORT.KPI_CARDS.MISSED_LIVE_CHATS')"
          :value="summary.missed_live_chats?.value ?? 0"
          :previous-value="summary.missed_live_chats?.previous"
          :change-pct="summary.missed_live_chats?.change_pct"
          :inverse-trend="true"
          :info-text="$t('REPORT.KPI_CARDS.INFO_MISSED_LIVE_CHATS')"
        />

        <!-- 5. Total Inbound Calls -->
        <KpiCard
          :label="$t('REPORT.KPI_CARDS.TOTAL_INBOUND_CALLS')"
          :value="summary.total_inbound_calls?.value ?? 0"
          :previous-value="summary.total_inbound_calls?.previous"
          :change-pct="summary.total_inbound_calls?.change_pct"
          :info-text="$t('REPORT.KPI_CARDS.INFO_TOTAL_INBOUND_CALLS')"
        />

        <!-- 6. Completed Inbound Calls -->
        <KpiCard
          :label="$t('REPORT.KPI_CARDS.COMPLETED_CALLS')"
          :value="summary.completed_inbound_calls?.value ?? 0"
          :previous-value="summary.completed_inbound_calls?.previous"
          :change-pct="summary.completed_inbound_calls?.change_pct"
          :info-text="$t('REPORT.KPI_CARDS.INFO_COMPLETED_CALLS')"
        />

        <!-- 7. Inbound Call Completion Rate -->
        <KpiCard
          :label="$t('REPORT.KPI_CARDS.INBOUND_COMPLETION_RATE')"
          :value="summary.inbound_completion_rate?.value ?? 0"
          unit="%"
          :previous-value="summary.inbound_completion_rate?.previous"
          :change-pct="summary.inbound_completion_rate?.change_pct"
          :numerator="summary.completed_inbound_calls?.value"
          :denominator="summary.total_inbound_calls?.value"
          :info-text="$t('REPORT.KPI_CARDS.INFO_INBOUND_COMPLETION_RATE')"
        />

        <!-- 8. Total Abandoned Calls -->
        <KpiCard
          :label="$t('REPORT.KPI_CARDS.ABANDONED_CALLS')"
          :value="summary.abandoned_calls?.value ?? 0"
          :previous-value="summary.abandoned_calls?.previous"
          :change-pct="summary.abandoned_calls?.change_pct"
          :inverse-trend="true"
          :info-text="$t('REPORT.KPI_CARDS.INFO_ABANDONED_CALLS')"
        />

        <!-- 9. Abandoned Call Rate -->
        <KpiCard
          :label="$t('REPORT.KPI_CARDS.ABANDONED_CALL_RATE')"
          :value="summary.abandoned_call_rate?.value ?? 0"
          unit="%"
          :previous-value="summary.abandoned_call_rate?.previous"
          :change-pct="summary.abandoned_call_rate?.change_pct"
          :inverse-trend="true"
          :numerator="summary.abandoned_calls?.value"
          :denominator="summary.total_inbound_calls?.value"
          :info-text="$t('REPORT.KPI_CARDS.INFO_ABANDONED_CALL_RATE')"
        />

        <!-- 10. Total Outbound Calls Made -->
        <KpiCard
          :label="$t('REPORT.KPI_CARDS.TOTAL_CALLS_MADE')"
          :value="summary.total_calls_made?.value ?? 0"
          :previous-value="summary.total_calls_made?.previous"
          :change-pct="summary.total_calls_made?.change_pct"
          :info-text="$t('REPORT.KPI_CARDS.INFO_TOTAL_CALLS_MADE')"
        />

        <!-- 11. Total TBP Orders -->
        <KpiCard
          :label="$t('REPORT.KPI_CARDS.TOTAL_TBP_ORDERS')"
          :value="summary.total_tbp_orders?.value ?? 0"
          :previous-value="summary.total_tbp_orders?.previous"
          :change-pct="summary.total_tbp_orders?.change_pct"
          :info-text="$t('REPORT.KPI_CARDS.INFO_TOTAL_TBP_ORDERS')"
        />

        <!-- Voicemails (Additional requested call metric) -->
        <KpiCard
          :label="$t('REPORT.KPI_CARDS.VOICEMAILS')"
          :value="summary.voicemails?.value ?? 0"
          :previous-value="summary.voicemails?.previous"
          :change-pct="summary.voicemails?.change_pct"
          :info-text="$t('REPORT.KPI_CARDS.INFO_VOICEMAILS')"
        />
      </div>

      <!-- Business Hours & Non-Business Hours Performance Cards -->
      <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3.5 mb-6">
        <KpiCard
          :label="$t('REPORT.KPI_CARDS.BH_CALLS')"
          :value="summary.total_calls_business_hours?.value ?? 0"
          :previous-value="summary.total_calls_business_hours?.previous"
          :percentage="summary.business_hours_completion_rate?.value"
          :info-text="$t('REPORT.KPI_CARDS.INFO_BH_CALLS')"
        />

        <KpiCard
          :label="$t('REPORT.KPI_CARDS.BH_COMPLETION_RATE')"
          :value="summary.business_hours_completion_rate?.value ?? 0"
          unit="%"
          :previous-value="summary.business_hours_completion_rate?.previous"
          :numerator="summary.answered_business_hours?.value"
          :denominator="summary.total_calls_business_hours?.value"
          :info-text="$t('REPORT.KPI_CARDS.INFO_BH_COMPLETION_RATE')"
        />

        <KpiCard
          :label="$t('REPORT.KPI_CARDS.BH_ABANDONMENT_RATE')"
          :value="summary.business_hours_abandonment_rate?.value ?? 0"
          unit="%"
          :previous-value="summary.business_hours_abandonment_rate?.previous"
          :inverse-trend="true"
          :numerator="summary.abandoned_business_hours?.value"
          :denominator="summary.total_calls_business_hours?.value"
          :info-text="$t('REPORT.KPI_CARDS.INFO_BH_ABANDONMENT_RATE')"
        />

        <KpiCard
          :label="$t('REPORT.KPI_CARDS.NBH_ABANDONMENT_RATE')"
          :value="summary.non_business_hours_abandonment_rate?.value ?? 0"
          unit="%"
          :previous-value="summary.non_business_hours_abandonment_rate?.previous"
          :inverse-trend="true"
          :numerator="summary.abandoned_non_business_hours?.value"
          :denominator="summary.total_calls_non_business_hours?.value"
          :info-text="$t('REPORT.KPI_CARDS.INFO_NBH_ABANDONMENT_RATE')"
        />
      </div>
    </section>

    <!-- Detailed Reporting Table -->
    <section>
      <KpiTable
        :weekly-data="weeklyData"
        :is-loading="isLoading"
      />
    </section>
  </div>
</template>
